import React, {useEffect, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faBinoculars,
    faCalendarDays,
    faCompass,
    faLocationDot,
    faMapLocationDot,
    faTableCellsLarge
} from "@fortawesome/free-solid-svg-icons";
import {Link, useNavigate} from "react-router-dom";
import * as localforage from "localforage";
import {
    buildLifeJourney,
    getCountryCodeFromStateProvince,
    getCountyKey,
    getUniqueChecklistCount,
    getYear,
    hydrateJourneyMoments,
    serializeJourneyMoments
} from "../helpers";
import {LIFE_JOURNEY_CACHE_KEY, LIFE_JOURNEY_CACHE_VERSION, LIFE_JOURNEY_HERO_IMAGE} from "../constants";
import type {CachedLifeJourney, DiscoverProps, JourneyMoment} from "../types";
import './life-journey.scss';

export default function LifeJourney({rawData, lifeList, observationsByLocation, checklist, speciesImages, homeLocation}: DiscoverProps) {
    const navigate = useNavigate();
    const [moments, setMoments] = useState<JourneyMoment[]>([]);
    const [isBuildingJourney, setIsBuildingJourney] = useState(true);
    const lifeSpeciesCount = lifeList.length;
    const stateProvinceCount = new Set(rawData.map(observation => observation.stateProvince).filter(Boolean)).size;
    const countyCount = new Set(rawData.map(getCountyKey).filter(Boolean)).size;
    const countryCount = new Set(rawData.map(getCountryCodeFromStateProvince).filter(Boolean)).size;
    const ecoDataLocationCount = observationsByLocation.filter(locationGroup => 'ecoData' in locationGroup && locationGroup.ecoData).length;

    useEffect(() => {
        let isActive = true;
        setIsBuildingJourney(true);

        if (rawData.length && !observationsByLocation.length) {
            return () => {
                isActive = false;
            };
        }

        const timeoutId = window.setTimeout(() => {
            const resolveJourney = async () => {
                try {
                    const cachedJourney: CachedLifeJourney | null = await localforage.getItem(LIFE_JOURNEY_CACHE_KEY);

                    if (
                        cachedJourney?.version === LIFE_JOURNEY_CACHE_VERSION
                        && cachedJourney.ecoDataLocationCount === ecoDataLocationCount
                        && cachedJourney.moments?.length
                    ) {
                        if (!isActive) return;

                        setMoments(hydrateJourneyMoments(cachedJourney.moments));
                        setIsBuildingJourney(false);
                        return;
                    }

                    const journeyMoments = buildLifeJourney(rawData, checklist, speciesImages, homeLocation, observationsByLocation);

                    await localforage.setItem(LIFE_JOURNEY_CACHE_KEY, {
                        version: LIFE_JOURNEY_CACHE_VERSION,
                        ecoDataLocationCount,
                        moments: serializeJourneyMoments(journeyMoments)
                    });

                    if (!isActive) return;

                    setMoments(journeyMoments);
                    setIsBuildingJourney(false);
                } catch (error) {
                    console.error(error);
                    const journeyMoments = buildLifeJourney(rawData, checklist, speciesImages, homeLocation, observationsByLocation);

                    if (!isActive) return;

                    setMoments(journeyMoments);
                    setIsBuildingJourney(false);
                }
            }

            void resolveJourney();
        }, 0);

        return () => {
            isActive = false;
            window.clearTimeout(timeoutId);
        };
    }, [rawData, observationsByLocation, checklist, speciesImages, homeLocation, ecoDataLocationCount]);

    return (
        <div className="discover discover-journey">
            <button type="button" className="discover__back" onClick={() => navigate('/discover')}>
                <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                Discover
            </button>
            <section className="journey-hero" style={{'--discover-hero-image': `url(${LIFE_JOURNEY_HERO_IMAGE})`} as React.CSSProperties}>
                <div>
                    <p className="discover__eyebrow">Life Journey</p>
                    <h1>My Life Journey</h1>
                    <p>A timeline of your birding life, one moment at a time.</p>
                </div>
            </section>
            <section className="journey-stats" aria-label="Journey summary">
                <div><FontAwesomeIcon icon={faCalendarDays} /><strong>{new Set(rawData.map(observation => getYear(observation.date)).filter(Boolean)).size.toLocaleString()}</strong><span>Years Birding</span></div>
                <div><FontAwesomeIcon icon={faBinoculars} /><strong>{lifeSpeciesCount.toLocaleString()}</strong><span>Species Seen</span></div>
                <div><FontAwesomeIcon icon={faTableCellsLarge} /><strong>{getUniqueChecklistCount(rawData).toLocaleString()}</strong><span>Checklists</span></div>
                <div><FontAwesomeIcon icon={faMapLocationDot} /><strong>{countyCount.toLocaleString()}</strong><span>Counties</span></div>
                <div><FontAwesomeIcon icon={faLocationDot} /><strong>{stateProvinceCount.toLocaleString()}</strong><span>States/Provinces</span></div>
                <div><FontAwesomeIcon icon={faCompass} /><strong>{countryCount.toLocaleString()}</strong><span>Countries</span></div>
            </section>
            {isBuildingJourney ?
                <div className="journey-loading" role="status" aria-live="polite" aria-busy="true">
                    <span className="journey-loading__spinner" aria-hidden="true" />
                    <strong>Building your life journey</strong>
                    <span>Reviewing sightings, milestones, places, and firsts.</span>
                </div> :
                <div className="journey-layout">
                    <ol className="journey-timeline">
                        {moments.map(moment => (
                            <li key={moment.id}>
                                <time>
                                    <strong>{moment.year || 'Insight'}</strong>
                                    <span>{moment.dateLabel}</span>
                                </time>
                                <span className="journey-timeline__marker" aria-hidden="true">
                                    <FontAwesomeIcon icon={moment.icon} />
                                </span>
                                <article className={`journey-timeline__card--${moment.kind} ${moment.image ? '' : 'journey-timeline__card--text'}`}>
                                    <div>
                                        <span className="journey-timeline__kind">{moment.kind}</span>
                                        <h2>{moment.title}</h2>
                                        <p>{moment.description}</p>
                                        {moment.locationLink ? (
                                            <Link className="journey-timeline__location-link" to={moment.locationLink.to}>
                                                {moment.locationLink.label}
                                            </Link>
                                        ) : null}
                                        {moment.detail ? <strong>{moment.detail}</strong> : null}
                                        {moment.detailList?.items.length ? (
                                            <ol className="journey-timeline__detail-list" start={moment.detailList.start}>
                                                {moment.detailList.items.map(item => (
                                                    <li key={item}>{item}</li>
                                                ))}
                                            </ol>
                                        ) : null}
                                    </div>
                                    {moment.image ? <img src={moment.image} alt={moment.detail ?? moment.title} /> : null}
                                </article>
                            </li>
                        ))}
                    </ol>
                </div>
            }
        </div>
    );
}
